import { Icon as Iconify } from "@iconify-icon/react";
import {
  Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Box, CircularProgress, Grid, Tooltip
} from "@mui/material";
import { useState, useEffect } from "react";

/**
 * Props:
 *  open: boolean
 *  onClose: fn()
 *  onPick: fn({ iconId, color })
 *  color?: initial hex string (e.g. "#6a2135")
 */
export default function IconPickerDialog({ open, onClose, onPick, color = "#6a2135" }) {
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [icons, setIcons] = useState([]);
  const [localColor, setLocalColor] = useState(color);

  useEffect(() => { if (open) setLocalColor(color || "#6a2135"); }, [open, color]);

  const PREFIXES = ["mdi", "tabler", "material-symbols"];
  const FALLBACK = [
    "mdi:home", "mdi:book-open", "mdi:account", "mdi:calendar",
    "tabler:brand-github", "tabler:folder", "tabler:layout-dashboard", "tabler:settings",
    "material-symbols:check-circle-rounded", "material-symbols:star-rounded", "material-symbols:share",
  ];

  useEffect(() => {
    let live = true;
    if (!open) return;
    const run = async () => {
      setLoading(true);
      try {
        const q = encodeURIComponent((term || "book").trim());
        const prefixes = encodeURIComponent(PREFIXES.join(","));
        const url = `https://api.iconify.design/search?query=${q}&limit=120&prefixes=${prefixes}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Iconify search failed");
        const data = await res.json();
        const list = Array.isArray(data?.icons) ? data.icons.slice(0, 300) : [];
        if (live) setIcons(list.length ? list : FALLBACK);
      } catch {
        if (live) setIcons(FALLBACK);
      } finally {
        if (live) setLoading(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => { live = false; clearTimeout(t); };
  }, [term, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Välj ikon & färg</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            fullWidth autoFocus
            placeholder="Sök (t.ex. book, home, calendar)"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <Tooltip title="Ikonfärg">
            <input
              type="color"
              aria-label="Ikonfärg"
              value={localColor}
              onChange={(e) => setLocalColor(e.target.value)}
              style={{
                width: 44, height: 44, padding: 0,
                border: "1px solid #ddd", borderRadius: 8, background: "transparent", cursor: "pointer"
              }}
            />
          </Tooltip>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={1.5}>
            {icons.map((id) => (
              <Grid item key={id}>
                <IconButton
                  onClick={() => { onPick?.({ iconId: id, color: localColor }); onClose?.(); }}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, width: 56, height: 56 }}
                >
                  <Iconify icon={id} width={28} height={28} style={{ color: localColor }} />
                </IconButton>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}
